import { yupResolver } from '@hookform/resolvers/yup'
import { Close } from '@mui/icons-material'
import {
  Box,
  Button,
  darken,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  Typography,
} from '@mui/material'
import * as Yup from 'yup'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { axiosPatch } from 'connectors/axiosPatch'
import { useAuthContext } from 'context/AuthContext'
import PageLoader from 'components/PageLoader'
import { UpdateStatusData } from 'pages/Applications/Applications.types'
import { StatusTypes } from 'pages/Applications/applicationStatuses'
import { useAlert } from 'context/AlertContext'
import { RejectReasonsSelect } from './reject-resons-select'

type FormFields = {
  reason: string
}

type RejectButtonProps = {
  id: number
  langId: number
}

export const RejectButton = ({ id, langId }: RejectButtonProps) => {
  const { t } = useTranslation()
  const { changeMessage } = useAlert()
  const { token, refreshToken } = useAuthContext()
  const [modalOpen, setModalOpen] = useState(false)
  const queryClient = useQueryClient()

  const {
    mutate: updateStatusMutate,
    data: updateStatusResponse,
    isLoading: isUpdating,
  } = useMutation(
    async ({ id, newStatusId, reason }: UpdateStatusData) =>
      await axiosPatch<{ affected: number }>(token, refreshToken, `applications/status/${id}`, {
        status: newStatusId,
        reason,
      }),
    {
      onSuccess: (response, { newStatusId }) => {
        queryClient.refetchQueries({ queryKey: ['application-details'], exact: false })
        setModalOpen(false)
      },
    }
  )

  useEffect(() => {
    if (typeof updateStatusResponse !== 'string' && updateStatusResponse !== undefined) {
      if ('error' in updateStatusResponse) {
        changeMessage(JSON.stringify(updateStatusResponse?.error?.data?.message), 'error')
      }
      if (!('error' in updateStatusResponse) && updateStatusResponse.affected > 0) {
        changeMessage(t('common.success'), 'success')
      }
    }
    queryClient.refetchQueries({ queryKey: ['application-details', id], exact: false })
  }, [updateStatusResponse])

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormFields>({
    defaultValues: {
      reason: '',
    },
    resolver: yupResolver(
      Yup.object().shape({
        reason: Yup.string().required(t('common.required').toString()),
      })
    ),
  })

  const openModal = () => {
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
  }

  const onSubmit = (data: FormFields) => {
    updateStatusMutate({
      id,
      newStatusId: StatusTypes.Rejected,
      reason: data.reason,
    })
  }

  return (
    <>
      <Button size="small" variant="outlined" color="error" onClick={openModal}>
        {t('application.reject')}
      </Button>
      {isUpdating && <PageLoader />}
      {modalOpen && (
        <Dialog PaperProps={{ sx: { width: '100%', maxWidth: 400, p: 2 } }} open={modalOpen} onClose={closeModal}>
          <DialogTitle sx={{ justifyContent: 'space-between', display: 'flex', alignItems: 'flex-start', pb: 0 }}>
            <Box
              flexShrink={0}
              flexGrow={0}
              width="64px"
              height="64px"
              left={-16}
              top={-12}
              position="relative"
              sx={({ palette }) => ({
                boxShadow: `
                inset 0 0 90px #FEE4E2, 
                0 0 0 16px ${palette.background.paper}, 
                0 0 0 17px ${darken(palette.background.default, 0.05)}, 
                0 0 0 33px ${palette.background.paper}, 
                0 0 0 34px ${darken(palette.background.default, 0.03)}, 
                0 0 0 50px ${palette.background.paper}, 
                0 0 0 51px ${darken(palette.background.default, 0.01)},
                0 0 0 67px ${palette.background.paper}, 
                0 0 0 68px ${palette.background.default}`,
                border: `8px solid #FEE4E245;`,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              })}
            >
              <WarningAmberRounded fontSize="large" color="error" />
            </Box>
            <IconButton onClick={closeModal}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ px: 1, pb: 4, zIndex: 2 }}>
            <Typography fontWeight={600} textAlign="center" variant="h5" mb={1}>
              {t('application.rejestingTitle')}
            </Typography>
            <Typography textAlign="center" mb={2}>
              {t('application.rejectingDesc')}
            </Typography>
            <form id="reason-form" onSubmit={handleSubmit(onSubmit)}>
              <FormControl fullWidth error={Boolean(errors?.reason?.message)}>
                <RejectReasonsSelect label={t('application.rejectReason')} {...register('reason')} langId={langId} />
                {errors?.reason?.message !== undefined ? (
                  <FormHelperText>
                    <>{errors?.reason?.message}</>
                  </FormHelperText>
                ) : null}
              </FormControl>
            </form>
          </DialogContent>
          <DialogActions>
            <Button fullWidth type="button" color="secondary" variant="outlined" onClick={closeModal}>
              {t('common.cancel')}
            </Button>
            <Button fullWidth type="submit" variant="contained" form="reason-form">
              {t('common.reject')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  )
}
